import React from 'react';

/* ─── Shared mini-component helpers ─────────────────────────────── */
const Logo = () => (
  <g>
    <rect x="8" y="6" width="14" height="8" rx="2" fill="#10b981" opacity="0.9" />
    <rect x="24" y="7" width="28" height="2.5" rx="1.25" fill="#d1fae5" />
    <rect x="24" y="11" width="20" height="2" rx="1" fill="#d1fae5" opacity="0.6" />
  </g>
);

const SectionLabel = ({ y, label, width = 80 }) => (
  <g>
    <rect x="8" y={y} width={width} height="3" rx="1.5" fill="#374151" />
    <line x1="8" y1={y + 5} x2="152" y2={y + 5} stroke="#e5e7eb" strokeWidth="0.8" />
  </g>
);

const InputField = ({ y, label = '', labelWidth = 50, full = false }) => (
  <g>
    <rect x="8" y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x="8" y={y + 4} width={full ? 144 : 144} height="7" rx="2" fill="white" stroke="#e5e7eb" strokeWidth="0.8" />
  </g>
);

const HalfInputField = ({ y, label = '', labelWidth = 40, xOffset = 0 }) => (
  <g>
    <rect x={8 + xOffset} y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x={8 + xOffset} y={y + 4} width={68} height="7" rx="2" fill="white" stroke="#e5e7eb" strokeWidth="0.8" />
  </g>
);

const TextareaField = ({ y, label = '', labelWidth = 50, rows = 3 }) => (
  <g>
    <rect x="8" y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x="8" y={y + 4} width="144" height={rows * 5 + 4} rx="2" fill="white" stroke="#e5e7eb" strokeWidth="0.8" />
    <rect x="12" y={y + 7} width="80" height="1.5" rx="0.75" fill="#e5e7eb" />
    {rows > 1 && <rect x="12" y={y + 10} width="60" height="1.5" rx="0.75" fill="#e5e7eb" />}
    {rows > 2 && <rect x="12" y={y + 13} width="70" height="1.5" rx="0.75" fill="#e5e7eb" />}
  </g>
);

const SelectField = ({ y, label = '', labelWidth = 50 }) => (
  <g>
    <rect x="8" y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x="8" y={y + 4} width="144" height="7" rx="2" fill="white" stroke="#e5e7eb" strokeWidth="0.8" />
    <rect x="12" y={y + 6.5} width="50" height="2" rx="1" fill="#d1d5db" />
    <text x="146" y={y + 9.5} fontSize="5" fill="#9ca3af">▾</text>
  </g>
);

const RadioGroup = ({ y, label = '', labelWidth = 60, options = 3 }) => {
  const opts = Array.from({ length: options });
  return (
    <g>
      <rect x="8" y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
      {opts?.map((_, i) => (
        <g key={i}>
          <circle cx="12" cy={y + 7 + i * 6} r="2" fill="white" stroke="#d1d5db" strokeWidth="0.8" />
          <rect x="16" y={y + 5.5 + i * 6} width={30 + (i % 2) * 10} height="2" rx="1" fill="#e5e7eb" />
        </g>
      ))}
    </g>
  );
};

const CheckboxGroup = ({ y, label = '', labelWidth = 60, options = 3 }) => {
  const opts = Array.from({ length: options });
  return (
    <g>
      <rect x="8" y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
      {opts?.map((_, i) => (
        <g key={i}>
          <rect x="9.5" y={y + 5.5 + i * 6} width="4" height="4" rx="0.8" fill="white" stroke="#d1d5db" strokeWidth="0.8" />
          {i === 0 && <path d={`M10.5 ${y + 7 + i * 6} l1 1 l2 -2`} stroke="#10b981" strokeWidth="0.8" fill="none" />}
          <rect x="16" y={y + 6 + i * 6} width={28 + (i % 3) * 8} height="2" rx="1" fill="#e5e7eb" />
        </g>
      ))}
    </g>
  );
};

const SliderField = ({ y, label = '', labelWidth = 60, value = 0.4 }) => (
  <g>
    <rect x="8" y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x="8" y={y + 6} width="144" height="2" rx="1" fill="#e5e7eb" />
    <rect x="8" y={y + 6} width={144 * value} height="2" rx="1" fill="#10b981" opacity="0.5" />
    <circle cx={8 + 144 * value} cy={y + 7} r="3.5" fill="#10b981" />
    <rect x="8" y={y + 11} width="8" height="2" rx="1" fill="#d1d5db" />
    <rect x="148" y={y + 11} width="8" height="2" rx="1" fill="#d1d5db" />
  </g>
);

const FileUpload = ({ y, label = '', labelWidth = 50 }) => (
  <g>
    <rect x="8" y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x="8" y={y + 4} width="144" height="14" rx="2" fill="#f9fafb" stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,2" />
    <text x="80" y={y + 13} fontSize="5" fill="#9ca3af" textAnchor="middle">📎 Upload file</text>
  </g>
);

const SubmitBtn = ({ y }) => (
  <rect x="104" y={y} width="48" height="9" rx="3" fill="#10b981" />
);

const StarRating = ({ y, label = '', labelWidth = 50 }) => (
  <g>
    <rect x="8" y={y} width={labelWidth} height="2.5" rx="1.25" fill="#9ca3af" />
    {[0, 1, 2, 3, 4]?.map(i => (
      <text key={i} x={12 + i * 12} y={y + 12} fontSize="9" fill={i < 4 ? '#f59e0b' : '#d1d5db'}>★</text>
    ))}
  </g>
);

const SignatureField = ({ y }) => (
  <g>
    <rect x="8" y={y} width="40" height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x="8" y={y + 4} width="144" height="14" rx="2" fill="#f9fafb" stroke="#d1d5db" strokeWidth="0.8" />
    <path d={`M20 ${y + 14} q5 -6 10 0 q5 6 10 0 q5 -6 10 0`} stroke="#6b7280" strokeWidth="1" fill="none" opacity="0.5" />
    <rect x="8" y={y + 19} width="144" height="0.5" stroke="#d1d5db" strokeWidth="0.5" fill="none" />
  </g>
);

const CardInput = ({ y }) => (
  <g>
    <rect x="8" y={y} width="40" height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x="8" y={y + 4} width="144" height="9" rx="2" fill="white" stroke="#e5e7eb" strokeWidth="0.8" />
    <rect x="12" y={y + 7} width="8" height="3" rx="1" fill="#3b82f6" opacity="0.7" />
    <rect x="22" y={y + 7.5} width="30" height="2" rx="1" fill="#d1d5db" />
    <rect x="56" y={y + 7.5} width="15" height="2" rx="1" fill="#d1d5db" />
    <rect x="75" y={y + 7.5} width="10" height="2" rx="1" fill="#d1d5db" />
  </g>
);

const AmountDisplay = ({ y, amount = '$50.00' }) => (
  <g>
    <rect x="8" y={y} width="144" height="14" rx="3" fill="#ecfdf5" stroke="#6ee7b7" strokeWidth="0.8" />
    <text x="80" y={y + 9} fontSize="8" fill="#059669" textAnchor="middle" fontWeight="bold">{amount}</text>
  </g>
);

const CalendarWidget = ({ y }) => (
  <g>
    <rect x="8" y={y} width="144" height="40" rx="3" fill="white" stroke="#e5e7eb" strokeWidth="0.8" />
    {/* Header */}
    <rect x="8" y={y} width="144" height="10" rx="3" fill="#10b981" />
    <rect x="8" y={y + 7} width="144" height="3" fill="#10b981" />
    <text x="80" y={y + 7.5} fontSize="5" fill="white" textAnchor="middle">January 2026</text>
    {/* Day labels */}
    {['S','M','T','W','T','F','S']?.map((d, i) => (
      <text key={i} x={14 + i * 19} y={y + 16} fontSize="3.5" fill="#9ca3af" textAnchor="middle">{d}</text>
    ))}
    {/* Day cells */}
    {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21]?.map((d, i) => {
      const col = i % 7;
      const row = Math.floor(i / 7);
      const isSelected = d === 15;
      return (
        <g key={d}>
          {isSelected && <circle cx={14 + col * 19} cy={y + 22 + row * 7} r="4" fill="#10b981" />}
          <text x={14 + col * 19} y={y + 23.5 + row * 7} fontSize="3.5" fill={isSelected ? 'white' : '#374151'} textAnchor="middle">{d}</text>
        </g>
      );
    })}
  </g>
);

const TimeSlots = ({ y }) => (
  <g>
    <rect x="8" y={y} width="2.5" height="2.5" rx="1.25" fill="#9ca3af" />
    <rect x="8" y={y} width="40" height="2.5" rx="1.25" fill="#9ca3af" />
    {['9:00 AM', '10:00 AM', '11:00 AM', '2:00 PM']?.map((t, i) => (
      <g key={i}>
        <rect x={8 + i * 37} y={y + 5} width="33" height="8" rx="2"
          fill={i === 1 ? '#10b981' : 'white'}
          stroke={i === 1 ? '#10b981' : '#e5e7eb'}
          strokeWidth="0.8" />
        <text x={8 + i * 37 + 16.5} y={y + 10.5} fontSize="3.5"
          fill={i === 1 ? 'white' : '#6b7280'}
          textAnchor="middle">{t}</text>
      </g>
    ))}
  </g>
);

const ProgressBar = ({ y, step = 2, total = 3 }) => (
  <g>
    <rect x="8" y={y} width="144" height="3" rx="1.5" fill="#e5e7eb" />
    <rect x="8" y={y} width={144 * (step / total)} height="3" rx="1.5" fill="#10b981" />
    <text x="80" y={y - 2} fontSize="3.5" fill="#9ca3af" textAnchor="middle">Step {step} of {total}</text>
  </g>
);

const NpsRow = ({ y }) => (
  <g>
    <rect x="8" y={y} width="80" height="2.5" rx="1.25" fill="#9ca3af" />
    {[0,1,2,3,4,5,6,7,8,9,10]?.map(n => (
      <g key={n}>
        <rect x={8 + n * 13} y={y + 5} width="11" height="8" rx="1.5"
          fill={n >= 9 ? '#d1fae5' : n >= 7 ? '#fef9c3' : '#fee2e2'}
          stroke={n === 9 ? '#10b981' : '#e5e7eb'}
          strokeWidth="0.6" />
        <text x={8 + n * 13 + 5.5} y={y + 10.5} fontSize="3.5" fill="#374151" textAnchor="middle">{n}</text>
      </g>
    ))}
  </g>
);

const DonationAmounts = ({ y }) => (
  <g>
    <rect x="8" y={y} width="40" height="2.5" rx="1.25" fill="#9ca3af" />
    {['$10', '$25', '$50', '$100']?.map((a, i) => (
      <g key={i}>
        <rect x={8 + i * 37} y={y + 5} width="33" height="9" rx="2"
          fill={i === 2 ? '#10b981' : 'white'}
          stroke={i === 2 ? '#10b981' : '#e5e7eb'}
          strokeWidth="0.8" />
        <text x={8 + i * 37 + 16.5} y={y + 11} fontSize="4"
          fill={i === 2 ? 'white' : '#374151'}
          textAnchor="middle" fontWeight={i === 2 ? 'bold' : 'normal'}>{a}</text>
      </g>
    ))}
  </g>
);

const PlanCards = ({ y }) => (
  <g>
    {['Basic\n$29/mo', 'Standard\n$49/mo', 'Premium\n$79/mo']?.map((p, i) => {
      const lines = p?.split('\n');
      return (
        <g key={i}>
          <rect x={8 + i * 50} y={y} width="46" height="22" rx="3"
            fill={i === 1 ? '#ecfdf5' : 'white'}
            stroke={i === 1 ? '#10b981' : '#e5e7eb'}
            strokeWidth={i === 1 ? 1.2 : 0.8} />
          {i === 1 && <rect x={8 + i * 50 + 8} y={y - 3} width="30" height="5" rx="2.5" fill="#10b981" />}
          {i === 1 && <text x={8 + i * 50 + 23} y={y + 0.5} fontSize="3" fill="white" textAnchor="middle">Popular</text>}
          <text x={8 + i * 50 + 23} y={y + 10} fontSize="4" fill="#374151" textAnchor="middle">{lines?.[0]}</text>
          <text x={8 + i * 50 + 23} y={y + 17} fontSize="4.5" fill={i === 1 ? '#059669' : '#6b7280'} textAnchor="middle" fontWeight="bold">{lines?.[1]}</text>
        </g>
      );
    })}
  </g>
);

const EquipmentChecklist = ({ y }) => (
  <g>
    <rect x="8" y={y} width="60" height="2.5" rx="1.25" fill="#9ca3af" />
    {['Projector/Screen', 'Video Conferencing', 'Whiteboard', 'Catering Setup']?.map((item, i) => (
      <g key={i}>
        <rect x={9.5 + (i > 1 ? 76 : 0)} y={y + 5 + (i % 2) * 7} width="4" height="4" rx="0.8"
          fill={i < 2 ? '#10b981' : 'white'}
          stroke={i < 2 ? '#10b981' : '#d1d5db'}
          strokeWidth="0.8" />
        {i < 2 && <path d={`M10.5 ${y + 7 + (i % 2) * 7} l1 1 l2 -2`} stroke="white" strokeWidth="0.8" fill="none" />}
        <rect x={16 + (i > 1 ? 76 : 0)} y={y + 6.5 + (i % 2) * 7} width={30 + i * 3} height="2" rx="1" fill="#e5e7eb" />
      </g>
    ))}
  </g>
);

const BugSeverity = ({ y }) => (
  <g>
    <rect x="8" y={y} width="30" height="2.5" rx="1.25" fill="#9ca3af" />
    {[
      { label: 'Critical', color: '#ef4444' },
      { label: 'High', color: '#f97316' },
      { label: 'Medium', color: '#eab308' },
      { label: 'Low', color: '#22c55e' },
    ]?.map((s, i) => (
      <g key={i}>
        <circle cx="12" cy={y + 7 + i * 6} r="2" fill="white" stroke="#d1d5db" strokeWidth="0.8" />
        <rect x="16" y={y + 5.5 + i * 6} width="4" height="3" rx="1" fill={s?.color} opacity="0.8" />
        <rect x="22" y={y + 6 + i * 6} width={35 + i * 5} height="2" rx="1" fill="#e5e7eb" />
      </g>
    ))}
  </g>
);

const EnvDropdowns = ({ y }) => (
  <g>
    <SelectField y={y} label="Browser" labelWidth={30} />
    <SelectField y={y + 14} label="OS" labelWidth={20} />
  </g>
);

/* ─── Per-template preview renderers ────────────────────────────── */
const previews = {
  // 1. Contact Us Form
  1: () => (
    <svg viewBox="0 0 160 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="200" fill="#f9fafb" />
      <Logo />
      <InputField y={20} labelWidth={40} />
      <InputField y={36} labelWidth={50} />
      <InputField y={52} labelWidth={45} />
      <InputField y={68} labelWidth={35} />
      <TextareaField y={84} labelWidth={35} rows={4} />
      <SubmitBtn y={118} />
    </svg>
  ),

  // 2. Lead Capture Form
  2: () => (
    <svg viewBox="0 0 160 210" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="210" fill="#f9fafb" />
      <Logo />
      <ProgressBar y={20} step={1} total={3} />
      <SectionLabel y={28} label="Contact Information" width={90} />
      <InputField y={38} labelWidth={40} />
      <InputField y={54} labelWidth={50} />
      <InputField y={70} labelWidth={45} />
      <SectionLabel y={84} label="Company Details" width={80} />
      <InputField y={94} labelWidth={55} />
      <SelectField y={110} labelWidth={50} />
      <SelectField y={126} labelWidth={35} />
      <SectionLabel y={142} label="Project Information" width={85} />
      <RadioGroup y={152} labelWidth={55} options={3} />
      <SubmitBtn y={192} />
    </svg>
  ),

  // 3. Customer Feedback Survey
  3: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Overall Satisfaction" width={90} />
      <RadioGroup y={30} labelWidth={70} options={4} />
      <NpsRow y={58} />
      <SectionLabel y={76} label="Product Experience" width={85} />
      <CheckboxGroup y={86} labelWidth={65} options={3} />
      <RadioGroup y={110} labelWidth={55} options={3} />
      <SectionLabel y={132} label="Support & Service" width={80} />
      <RadioGroup y={142} labelWidth={60} options={3} />
      <SectionLabel y={164} label="Help Us Improve" width={75} />
      <TextareaField y={174} labelWidth={55} rows={3} />
      <SubmitBtn y={200} />
    </svg>
  ),

  // 4. Appointment Booking
  4: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Select Your Service" width={85} />
      <SelectField y={30} labelWidth={55} />
      <CalendarWidget y={44} />
      <TimeSlots y={90} />
      <SectionLabel y={106} label="Your Contact Details" width={90} />
      <InputField y={116} labelWidth={40} />
      <InputField y={132} labelWidth={50} />
      <InputField y={148} labelWidth={45} />
      <TextareaField y={164} labelWidth={65} rows={2} />
      <SubmitBtn y={186} />
    </svg>
  ),

  // 5. Payment Collection Form
  5: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Payment Information" width={85} />
      <AmountDisplay y={30} amount="$150.00" />
      <SelectField y={50} labelWidth={40} />
      <InputField y={66} labelWidth={65} />
      <SectionLabel y={82} label="Billing Address" width={75} />
      <InputField y={92} labelWidth={40} />
      <InputField y={108} labelWidth={50} />
      <InputField y={124} labelWidth={55} />
      <InputField y={140} labelWidth={35} />
      <SelectField y={156} labelWidth={35} />
      <CardInput y={172} />
      <CheckboxGroup y={188} labelWidth={80} options={1} />
      <SubmitBtn y={200} />
    </svg>
  ),

  // 6. Event Registration
  6: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Event Registration" width={85} />
      <SelectField y={30} labelWidth={55} />
      <InputField y={46} labelWidth={65} />
      <SectionLabel y={62} label="Primary Attendee" width={85} />
      <InputField y={72} labelWidth={40} />
      <InputField y={88} labelWidth={50} />
      <InputField y={104} labelWidth={45} />
      <SectionLabel y={120} label="Dietary & Accessibility" width={95} />
      <CheckboxGroup y={130} labelWidth={65} options={3} />
      <SectionLabel y={154} label="Workshop & Sessions" width={90} />
      <CheckboxGroup y={164} labelWidth={70} options={3} />
      <SubmitBtn y={196} />
    </svg>
  ),

  // 7. Job Application Form
  7: () => (
    <svg viewBox="0 0 160 230" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="230" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Personal Information" width={85} />
      <InputField y={30} labelWidth={40} />
      <InputField y={46} labelWidth={50} />
      <InputField y={62} labelWidth={45} />
      <SectionLabel y={78} label="Position Applied For" width={90} />
      <SelectField y={88} labelWidth={40} />
      <SelectField y={104} labelWidth={70} />
      <InputField y={120} labelWidth={70} />
      <SectionLabel y={136} label="Educational Background" width={95} />
      <SelectField y={146} labelWidth={55} />
      <InputField y={162} labelWidth={65} />
      <SectionLabel y={178} label="Required Documents" width={85} />
      <FileUpload y={188} labelWidth={50} />
      <FileUpload y={208} labelWidth={55} />
      <SubmitBtn y={222} />
    </svg>
  ),

  // 8. Product Order Form
  8: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Select Your Products" width={90} />
      <SelectField y={30} labelWidth={40} />
      <InputField y={46} labelWidth={40} />
      <SelectField y={62} labelWidth={35} />
      <SelectField y={78} labelWidth={30} />
      <SectionLabel y={94} label="Your Information" width={80} />
      <InputField y={104} labelWidth={40} />
      <InputField y={120} labelWidth={50} />
      <SectionLabel y={136} label="Shipping Address" width={80} />
      <InputField y={146} labelWidth={55} />
      <InputField y={162} labelWidth={30} />
      <SectionLabel y={178} label="Shipping Options" width={80} />
      <RadioGroup y={188} labelWidth={55} options={3} />
      <SubmitBtn y={210} />
    </svg>
  ),

  // 9. Newsletter Signup
  9: () => (
    <svg viewBox="0 0 160 180" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="180" fill="#f9fafb" />
      {/* Hero banner */}
      <rect x="0" y="0" width="160" height="50" fill="#ecfdf5" />
      <rect x="8" y="8" width="14" height="8" rx="2" fill="#10b981" opacity="0.9" />
      <rect x="24" y="9" width="28" height="2.5" rx="1.25" fill="#6ee7b7" />
      <text x="80" y="32" fontSize="8" fill="#059669" textAnchor="middle" fontWeight="bold">Subscribe to Our Newsletter</text>
      <text x="80" y="42" fontSize="4" fill="#6b7280" textAnchor="middle">Get the latest updates delivered to your inbox</text>
      <InputField y={58} labelWidth={40} />
      <InputField y={74} labelWidth={50} />
      <CheckboxGroup y={90} labelWidth={70} options={4} />
      <RadioGroup y={118} labelWidth={55} options={3} />
      <CheckboxGroup y={140} labelWidth={80} options={1} />
      <SubmitBtn y={154} />
    </svg>
  ),

  // 10. Support Ticket Form
  10: () => (
    <svg viewBox="0 0 160 210" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="210" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Your Contact Information" width={100} />
      <InputField y={30} labelWidth={40} />
      <InputField y={46} labelWidth={50} />
      <InputField y={62} labelWidth={65} />
      <SectionLabel y={78} label="Describe Your Issue" width={85} />
      <SelectField y={88} labelWidth={55} />
      {/* Priority with color indicators */}
      <rect x="8" y={104} width="40" height="2.5" rx="1.25" fill="#9ca3af" />
      {[
        { label: 'Low', color: '#22c55e' },
        { label: 'Medium', color: '#eab308' },
        { label: 'High', color: '#f97316' },
        { label: 'Urgent', color: '#ef4444' },
      ]?.map((p, i) => (
        <g key={i}>
          <circle cx="12" cy={110 + i * 6} r="2" fill="white" stroke="#d1d5db" strokeWidth="0.8" />
          <rect x="16" y={108.5 + i * 6} width="6" height="3" rx="1" fill={p?.color} opacity="0.8" />
          <rect x="24" y={109 + i * 6} width={30 + i * 5} height="2" rx="1" fill="#e5e7eb" />
        </g>
      ))}
      <InputField y={136} labelWidth={40} />
      <TextareaField y={152} labelWidth={65} rows={3} />
      <FileUpload y={172} labelWidth={50} />
      <SubmitBtn y={192} />
    </svg>
  ),

  // 11. Quote Request Form
  11: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Your Information" width={75} />
      <InputField y={30} labelWidth={40} />
      <InputField y={46} labelWidth={50} />
      <InputField y={62} labelWidth={45} />
      <SectionLabel y={78} label="Tell Us About Your Project" width={100} />
      <SelectField y={88} labelWidth={55} />
      <InputField y={104} labelWidth={55} />
      <TextareaField y={120} labelWidth={65} rows={3} />
      <RadioGroup y={142} labelWidth={55} options={4} />
      <SectionLabel y={170} label="Project Timeline" width={75} />
      <InputField y={180} labelWidth={65} />
      <SelectField y={196} labelWidth={70} />
      <SubmitBtn y={210} />
    </svg>
  ),

  // 12. Webinar Registration
  12: () => (
    <svg viewBox="0 0 160 210" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="210" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Choose Your Session" width={85} />
      {/* Session cards */}
      {['Session 1: Jan 15 — 10:00 AM', 'Session 2: Jan 15 — 2:00 PM', 'Session 3: Jan 16 — 10:00 AM']?.map((s, i) => (
        <g key={i}>
          <rect x="8" y={30 + i * 14} width="144" height="11" rx="2"
            fill={i === 1 ? '#ecfdf5' : 'white'}
            stroke={i === 1 ? '#10b981' : '#e5e7eb'}
            strokeWidth="0.8" />
          <circle cx="14" cy={35.5 + i * 14} r="2" fill={i === 1 ? '#10b981' : 'white'} stroke={i === 1 ? '#10b981' : '#d1d5db'} strokeWidth="0.8" />
          <rect x="19" y={34 + i * 14} width={70 + i * 5} height="2" rx="1" fill={i === 1 ? '#6ee7b7' : '#e5e7eb'} />
        </g>
      ))}
      <SelectField y={74} labelWidth={55} />
      <SectionLabel y={90} label="Attendee Information" width={85} />
      <InputField y={100} labelWidth={40} />
      <InputField y={116} labelWidth={50} />
      <InputField y={132} labelWidth={45} />
      <SectionLabel y={148} label="Notification Preferences" width={95} />
      <CheckboxGroup y={158} labelWidth={65} options={3} />
      <TextareaField y={182} labelWidth={70} rows={2} />
      <SubmitBtn y={198} />
    </svg>
  ),

  // 13. Patient Intake Form
  13: () => (
    <svg viewBox="0 0 160 230" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="230" fill="#f9fafb" />
      {/* Medical cross header */}
      <rect x="0" y="0" width="160" height="18" fill="#ecfdf5" />
      <rect x="8" y="4" width="14" height="8" rx="2" fill="#10b981" opacity="0.9" />
      <rect x="24" y="5" width="28" height="2.5" rx="1.25" fill="#6ee7b7" />
      <text x="148" y="10" fontSize="10" fill="#10b981" textAnchor="end">✚</text>
      <SectionLabel y={22} label="Patient Demographics" width={90} />
      <InputField y={32} labelWidth={40} />
      <InputField y={48} labelWidth={50} />
      <RadioGroup y={64} labelWidth={35} options={3} />
      <InputField y={86} labelWidth={50} />
      <SectionLabel y={102} label="Insurance Information" width={85} />
      <InputField y={112} labelWidth={65} />
      <InputField y={128} labelWidth={55} />
      <SectionLabel y={144} label="Medical History" width={75} />
      <CheckboxGroup y={154} labelWidth={80} options={4} />
      <TextareaField y={180} labelWidth={65} rows={2} />
      <SectionLabel y={198} label="Consent & Authorization" width={95} />
      <CheckboxGroup y={208} labelWidth={80} options={2} />
      <SignatureField y={218} />
      <SubmitBtn y={238} />
    </svg>
  ),

  // 14. Volunteer Application
  14: () => (
    <svg viewBox="0 0 160 210" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="210" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="About You" width={60} />
      <InputField y={30} labelWidth={40} />
      <InputField y={46} labelWidth={50} />
      <InputField y={62} labelWidth={45} />
      <SectionLabel y={78} label="Your Availability" width={80} />
      {/* Days of week grid */}
      <rect x="8" y={88} width="40" height="2.5" rx="1.25" fill="#9ca3af" />
      {['M','T','W','T','F','S','S']?.map((d, i) => (
        <g key={i}>
          <rect x={8 + i * 21} y={93} width="18" height="10" rx="2"
            fill={i < 5 ? '#ecfdf5' : 'white'}
            stroke={i < 5 ? '#10b981' : '#e5e7eb'}
            strokeWidth="0.8" />
          <text x={8 + i * 21 + 9} y={99.5} fontSize="4" fill={i < 5 ? '#059669' : '#9ca3af'} textAnchor="middle">{d}</text>
        </g>
      ))}
      <RadioGroup y={108} labelWidth={70} options={3} />
      <SectionLabel y={130} label="Skills & Interests" width={80} />
      <CheckboxGroup y={140} labelWidth={65} options={4} />
      <SelectField y={168} labelWidth={70} />
      <TextareaField y={184} labelWidth={70} rows={2} />
      <SubmitBtn y={200} />
    </svg>
  ),

  // 15. Employee Satisfaction Survey
  15: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Work Environment" width={80} />
      <RadioGroup y={30} labelWidth={70} options={4} />
      <SliderField y={56} labelWidth={70} value={0.65} />
      <RadioGroup y={72} labelWidth={80} options={3} />
      <SectionLabel y={96} label="Management & Leadership" width={100} />
      <RadioGroup y={106} labelWidth={80} options={4} />
      <SectionLabel y={132} label="Team & Culture" width={75} />
      <RadioGroup y={142} labelWidth={65} options={3} />
      <CheckboxGroup y={164} labelWidth={75} options={3} />
      <SectionLabel y={188} label="Growth & Development" width={90} />
      <RadioGroup y={198} labelWidth={80} options={3} />
      <SubmitBtn y={212} />
    </svg>
  ),

  // 16. Rental Application
  16: () => (
    <svg viewBox="0 0 160 230" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="230" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Applicant Information" width={90} />
      <InputField y={30} labelWidth={55} />
      <InputField y={46} labelWidth={50} />
      <InputField y={62} labelWidth={50} />
      <InputField y={78} labelWidth={55} />
      <SectionLabel y={94} label="Employment & Income" width={90} />
      <InputField y={104} labelWidth={65} />
      <InputField y={120} labelWidth={40} />
      <InputField y={136} labelWidth={65} />
      <SectionLabel y={152} label="Rental History" width={75} />
      <InputField y={162} labelWidth={65} />
      <InputField y={178} labelWidth={65} />
      <RadioGroup y={194} labelWidth={55} options={3} />
      <SectionLabel y={216} label="Authorization & Consent" width={95} />
      <CheckboxGroup y={220} labelWidth={80} options={2} />
      <SignatureField y={232} />
      <SubmitBtn y={248} />
    </svg>
  ),

  // 17. Donation Form
  17: () => (
    <svg viewBox="0 0 160 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="200" fill="#f9fafb" />
      {/* Heart header */}
      <rect x="0" y="0" width="160" height="20" fill="#ecfdf5" />
      <rect x="8" y="4" width="14" height="8" rx="2" fill="#10b981" opacity="0.9" />
      <text x="148" y="13" fontSize="12" fill="#10b981" textAnchor="end">♥</text>
      <SectionLabel y={24} label="Choose Your Donation Amount" width={110} />
      <DonationAmounts y={34} />
      <InputField y={50} labelWidth={65} />
      <RadioGroup y={66} labelWidth={65} options={3} />
      <SectionLabel y={90} label="Your Information" width={75} />
      <InputField y={100} labelWidth={40} />
      <InputField y={116} labelWidth={50} />
      <SectionLabel y={132} label="Tribute or Dedication (Optional)" width={115} />
      <RadioGroup y={142} labelWidth={65} options={3} />
      <InputField y={164} labelWidth={55} />
      <CheckboxGroup y={180} labelWidth={80} options={1} />
      <SubmitBtn y={192} />
    </svg>
  ),

  // 18. Conference Room Booking
  18: () => (
    <svg viewBox="0 0 160 210" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="210" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Select a Room" width={70} />
      {/* Room cards */}
      {['Board Room (20)', 'Meeting Room A (10)', 'Meeting Room B (8)']?.map((r, i) => (
        <g key={i}>
          <rect x="8" y={30 + i * 14} width="144" height="11" rx="2"
            fill={i === 0 ? '#ecfdf5' : 'white'}
            stroke={i === 0 ? '#10b981' : '#e5e7eb'}
            strokeWidth="0.8" />
          <rect x="12" y={34 + i * 14} width="6" height="3" rx="1" fill={i === 0 ? '#10b981' : '#d1d5db'} />
          <rect x="21" y={34.5 + i * 14} width={50 + i * 5} height="2" rx="1" fill={i === 0 ? '#6ee7b7' : '#e5e7eb'} />
        </g>
      ))}
      <InputField y={74} labelWidth={55} />
      <g>
        <HalfInputField y={90} labelWidth={40} />
        <HalfInputField y={90} labelWidth={40} xOffset={76} />
      </g>
      <InputField y={106} labelWidth={65} />
      <SectionLabel y={122} label="Equipment & Services" width={90} />
      <EquipmentChecklist y={132} />
      <SectionLabel y={152} label="Meeting Details" width={75} />
      <InputField y={162} labelWidth={55} />
      <InputField y={178} labelWidth={65} />
      <InputField y={194} labelWidth={50} />
      <SubmitBtn y={202} />
    </svg>
  ),

  // 19. Product Feedback Form
  19: () => (
    <svg viewBox="0 0 160 210" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="210" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Rate Your Purchase" width={80} />
      <SelectField y={30} labelWidth={70} />
      <StarRating y={46} labelWidth={55} />
      <SliderField y={64} labelWidth={65} value={0.7} />
      <SectionLabel y={82} label="Feature Evaluation" width={80} />
      <CheckboxGroup y={92} labelWidth={60} options={3} />
      <CheckboxGroup y={116} labelWidth={65} options={3} />
      <SectionLabel y={140} label="Write Your Review" width={75} />
      <InputField y={150} labelWidth={55} />
      <TextareaField y={166} labelWidth={60} rows={3} />
      <RadioGroup y={188} labelWidth={70} options={3} />
      <SubmitBtn y={200} />
    </svg>
  ),

  // 20. Scholarship Application
  20: () => (
    <svg viewBox="0 0 160 230" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="230" fill="#f9fafb" />
      {/* Academic header */}
      <rect x="0" y="0" width="160" height="18" fill="#ecfdf5" />
      <rect x="8" y="4" width="14" height="8" rx="2" fill="#10b981" opacity="0.9" />
      <text x="148" y="13" fontSize="10" fill="#10b981" textAnchor="end">🎓</text>
      <SectionLabel y={22} label="Applicant Information" width={85} />
      <InputField y={32} labelWidth={55} />
      <InputField y={48} labelWidth={50} />
      <InputField y={64} labelWidth={50} />
      <SectionLabel y={80} label="Academic Background" width={90} />
      <InputField y={90} labelWidth={70} />
      <SelectField y={106} labelWidth={55} />
      <InputField y={122} labelWidth={65} />
      <InputField y={138} labelWidth={55} />
      <SectionLabel y={154} label="Essay Questions" width={75} />
      <TextareaField y={164} labelWidth={80} rows={3} />
      <TextareaField y={186} labelWidth={80} rows={3} />
      <SectionLabel y={208} label="Required Documents" width={85} />
      <FileUpload y={218} labelWidth={65} />
      <SubmitBtn y={222} />
    </svg>
  ),

  // 21. Gym Membership Form
  21: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="Choose Your Membership" width={90} />
      <PlanCards y={30} />
      <SectionLabel y={58} label="Your Information" width={75} />
      <InputField y={68} labelWidth={40} />
      <InputField y={84} labelWidth={50} />
      <InputField y={100} labelWidth={50} />
      <InputField y={116} labelWidth={70} />
      <SectionLabel y={132} label="Health Screening" width={80} />
      <RadioGroup y={142} labelWidth={80} options={3} />
      <CheckboxGroup y={164} labelWidth={70} options={3} />
      <SectionLabel y={188} label="Your Fitness Goals" width={80} />
      <CheckboxGroup y={198} labelWidth={65} options={3} />
      <SignatureField y={212} />
      <SubmitBtn y={226} />
    </svg>
  ),

  // 22. Market Research Survey
  22: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="About You" width={55} />
      <RadioGroup y={30} labelWidth={45} options={4} />
      <RadioGroup y={56} labelWidth={35} options={3} />
      <SelectField y={78} labelWidth={70} />
      <SectionLabel y={94} label="Purchasing Habits" width={80} />
      <RadioGroup y={104} labelWidth={70} options={4} />
      <CheckboxGroup y={130} labelWidth={65} options={3} />
      <SliderField y={154} labelWidth={80} value={0.5} />
      <SectionLabel y={172} label="Brand Awareness" width={75} />
      <CheckboxGroup y={182} labelWidth={65} options={3} />
      <RadioGroup y={206} labelWidth={65} options={3} />
      <SubmitBtn y={212} />
    </svg>
  ),

  // 23. Partnership Inquiry Form
  23: () => (
    <svg viewBox="0 0 160 210" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="210" fill="#f9fafb" />
      <Logo />
      <SectionLabel y={20} label="About Your Company" width={85} />
      <InputField y={30} labelWidth={55} />
      <InputField y={46} labelWidth={65} />
      <SelectField y={62} labelWidth={40} />
      <InputField y={78} labelWidth={65} />
      <SectionLabel y={94} label="Primary Contact" width={75} />
      <InputField y={104} labelWidth={55} />
      <InputField y={120} labelWidth={40} />
      <InputField y={136} labelWidth={60} />
      <SectionLabel y={152} label="Partnership Proposal" width={85} />
      <CheckboxGroup y={162} labelWidth={70} options={4} />
      <TextareaField y={188} labelWidth={70} rows={3} />
      <SubmitBtn y={200} />
    </svg>
  ),

  // 24. Bug Report Form
  24: () => (
    <svg viewBox="0 0 160 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="160" height="220" fill="#f9fafb" />
      {/* Dev-style header */}
      <rect x="0" y="0" width="160" height="18" fill="#1f2937" />
      <rect x="8" y="5" width="6" height="6" rx="1" fill="#ef4444" />
      <rect x="16" y="5" width="6" height="6" rx="1" fill="#eab308" />
      <rect x="24" y="5" width="6" height="6" rx="1" fill="#22c55e" />
      <rect x="36" y="6.5" width="60" height="3" rx="1.5" fill="#374151" />
      <SectionLabel y={22} label="Bug Report Details" width={80} />
      <InputField y={32} labelWidth={40} />
      <BugSeverity y={48} />
      <SelectField y={78} labelWidth={65} />
      <SectionLabel y={94} label="Environment Details" width={80} />
      <EnvDropdowns y={104} />
      <InputField y={134} labelWidth={55} />
      <SectionLabel y={150} label="How to Reproduce" width={80} />
      <TextareaField y={160} labelWidth={55} rows={3} />
      <TextareaField y={182} labelWidth={65} rows={2} />
      <FileUpload y={200} labelWidth={65} />
      <SubmitBtn y={210} />
    </svg>
  ),
};

/* ─── Main export ────────────────────────────────────────────────── */
const FormLayoutGraphic = ({ formElements = [], compact = false, templateId }) => {
  const PreviewComponent = templateId && previews?.[templateId];

  if (PreviewComponent) {
    return (
      <div className="w-full h-full overflow-hidden">
        <PreviewComponent />
      </div>
    );
  }

  // Fallback: generic renderer for unknown templates
  const elements = formElements?.filter(el => el?.type !== 'media');
  const displayElements = compact ? elements?.slice(0, 7) : elements;

  return (
    <div className={`w-full h-full bg-white rounded overflow-hidden flex flex-col ${compact ? 'p-2 gap-1' : 'p-4 gap-2'}`}>
      <div className="flex items-center gap-1.5 border-b border-gray-100 pb-1.5 mb-0.5">
        <div className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} rounded bg-emerald-500 flex-shrink-0`} />
        <div className={`${compact ? 'h-1.5' : 'h-2'} bg-gray-200 rounded flex-1 max-w-[60px]`} />
      </div>
      {displayElements?.map((el, idx) => {
        const isHeading = el?.type === 'content';
        const isRadio = el?.type === 'radio';
        const isCheckbox = el?.type === 'checkbox';
        const isSelect = el?.type === 'select';
        const isTextarea = el?.multiline;
        const isFile = el?.type === 'file';
        const isSlider = el?.type === 'slider';

        if (isHeading) return (
          <div key={idx} className={`${compact ? 'mt-0.5' : 'mt-1'}`}>
            <div className={`${compact ? 'h-1.5 w-16' : 'h-2 w-24'} bg-gray-700 rounded`} />
          </div>
        );
        if (isRadio || isCheckbox) {
          const opts = el?.options?.slice(0, compact ? 2 : 3) || [];
          return (
            <div key={idx} className="flex flex-col gap-0.5">
              <div className={`${compact ? 'h-1 w-12' : 'h-1.5 w-16'} bg-gray-400 rounded mb-0.5`} />
              {opts?.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-1">
                  <div className={`${compact ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full border border-gray-300 flex-shrink-0`} />
                  <div className={`${compact ? 'h-1 w-8' : 'h-1.5 w-12'} bg-gray-200 rounded`} />
                </div>
              ))}
            </div>
          );
        }
        if (isSelect) return (
          <div key={idx} className="flex flex-col gap-0.5">
            <div className={`${compact ? 'h-1 w-10' : 'h-1.5 w-14'} bg-gray-400 rounded`} />
            <div className={`${compact ? 'h-3' : 'h-5'} border border-gray-200 rounded flex items-center justify-between px-1.5`}>
              <div className={`${compact ? 'h-1 w-8' : 'h-1.5 w-12'} bg-gray-200 rounded`} />
              <div className={`${compact ? 'text-[6px]' : 'text-[8px]'} text-gray-400`}>▾</div>
            </div>
          </div>
        );
        if (isTextarea) return (
          <div key={idx} className="flex flex-col gap-0.5">
            <div className={`${compact ? 'h-1 w-10' : 'h-1.5 w-14'} bg-gray-400 rounded`} />
            <div className={`${compact ? 'h-5' : 'h-8'} border border-gray-200 rounded`} />
          </div>
        );
        if (isFile) return (
          <div key={idx} className="flex flex-col gap-0.5">
            <div className={`${compact ? 'h-1 w-10' : 'h-1.5 w-14'} bg-gray-400 rounded`} />
            <div className={`${compact ? 'h-4' : 'h-6'} border border-dashed border-gray-300 rounded flex items-center justify-center`}>
              <div className={`${compact ? 'text-[6px]' : 'text-[8px]'} text-gray-400`}>📎 Upload</div>
            </div>
          </div>
        );
        if (isSlider) return (
          <div key={idx} className="flex flex-col gap-0.5">
            <div className={`${compact ? 'h-1 w-10' : 'h-1.5 w-14'} bg-gray-400 rounded`} />
            <div className={`${compact ? 'h-1.5' : 'h-2'} bg-gray-200 rounded-full relative`}>
              <div className="absolute left-1/3 top-1/2 -translate-y-1/2 w-2 h-2 bg-emerald-500 rounded-full" />
            </div>
          </div>
        );
        return (
          <div key={idx} className="flex flex-col gap-0.5">
            <div className={`${compact ? 'h-1 w-10' : 'h-1.5 w-14'} bg-gray-400 rounded`} />
            <div className={`${compact ? 'h-3' : 'h-5'} border border-gray-200 rounded bg-gray-50`} />
          </div>
        );
      })}
      <div className={`${compact ? 'mt-0.5' : 'mt-1'} flex justify-end`}>
        <div className={`${compact ? 'h-3 w-10' : 'h-5 w-16'} bg-emerald-500 rounded`} />
      </div>
      {compact && elements?.length > 7 && (
        <div className="text-center">
          <span className="text-[8px] text-gray-400">+{elements?.length - 7} more fields</span>
        </div>
      )}
    </div>
  );
};

export default FormLayoutGraphic;
